import { Button } from '@synoptic/ui-kit/button/button.js';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@synoptic/ui-kit/select/select.tsx';
import { clsx } from 'clsx';
import { LexicalEditor } from 'lexical';
import { FC, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { categorySelectOptions } from '@/components/streams/stream-categories.ts';
import {
  createStreamFormActions,
  formInfoBlock,
  formInfoDescription,
  formInfoTitle,
  columnFields,
  rowFields,
  streamFormActions,
  streamFormTrigger,
  checkbox,
} from '@/components/streams/stream-form/stream-form.css.ts';
import { StreamFormInputs } from '@/components/streams/stream-form/stream-form.tsx';
import { TextEditor } from '@/components/text-editor/text-editor.tsx';
import { stateToText } from '@/components/text-editor/utils/state-to-text.ts';
import { regexInputDelimiter } from './custom-stream-form.css.ts';

const TARGETS_MIN_ALLOWED_VALUE = 1;
const TARGETS_MAX_ALLOWED_VALUE = 100000;
const TOTAL_TARGETS_MAX_ALLOWED_VALUE = 100000;

export type CustomStreamMainDataStepProps = {
  hasInitialValues?: boolean;
  setNextStep: () => void;
};
export const CustomStreamMainDataStep: FC<CustomStreamMainDataStepProps> = ({
  hasInitialValues,
  setNextStep,
}) => {
  const isTablet = useIsTablet();

  const editorRef = useRef<LexicalEditor | null>(null);

  const { formState, register, control, getValues, setValue } =
    useFormContext<StreamFormInputs>();

  const handleNext = () => {
    if (getValues('customTargetsData.stepSize') == null) {
      const minTargets = getValues('customTargetsData.minTargets');
      if (minTargets != null) {
        setValue('customTargetsData.stepSize', minTargets);
      }
    }
    setNextStep();
  };

  return (
    <div className={columnFields}>
      <div className={formInfoBlock}>
        <h3 className={formInfoTitle}>Stream description</h3>
        <p className={formInfoDescription}>
          This is what buyers will see when considering your stream. Highlight
          what makes it valuable.
        </p>
      </div>

      <Input
        autoFocus={!hasInitialValues}
        {...register('name', {
          required: 'Name is required',
          maxLength: {
            value: 50,
            message: 'Name cannot exceed 50 characters',
          },
          validate: (v) => (v ? v.trim().length > 0 : undefined),
        })}
        maxLength={50}
        errorMessage={formState.errors.name?.message}
        placeholder={'e.g. “Scrap X accounts” or “BTC wallets”'}
        label={'Stream name'}
      />

      <Input
        {...register('label', {
          required: 'Label is required',
          maxLength: {
            value: 30,
            message: 'Label cannot exceed 30 characters',
          },
          validate: (v) => v.trim().length > 0,
        })}
        maxLength={30}
        errorMessage={formState.errors.label?.message}
        placeholder={'Enter the label here'}
        label={'Label'}
      />

      <Controller
        name={'streamCategory'}
        control={control}
        rules={{ required: 'Category is required' }}
        render={({ field }) => (
          <Select
            defaultValue={field.value}
            onValueChange={(state) => {
              field.onChange(state);
            }}
          >
            <SelectTrigger
              label={'Category'}
              placeholder={'Select category'}
              errorMessage={formState.errors.streamCategory?.message}
              className={streamFormTrigger}
              variant={'outline'}
            />
            <SelectContent position={'popper'} items={categorySelectOptions} />
          </Select>
        )}
      />

      <Controller
        name={'apiAccessEnabled'}
        control={control}
        render={({ field }) => (
          <label className={checkbox}>
            <Checkbox
              checked={field.value}
              onCheckedChange={(val) => field.onChange(val === true)}
            />
            Allow API access for this stream
          </label>
        )}
      />

      <Controller
        name="descriptionJson"
        control={control}
        rules={{
          validate: () => {
            if (editorRef.current) {
              const textContent = stateToText(
                editorRef.current.getEditorState(),
              ).trim();

              if (textContent.length == 0) {
                return 'Description is required';
              }

              if (textContent.length > 20000) {
                return 'Description cannot exceed 20000 characters';
              }

              return true;
            }
            return false;
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <TextEditor
              initialState={
                field.value
                  ? field.value
                  : formState.defaultValues?.descriptionJson
              }
              namespace={'stream-description'}
              editable
              editorRef={(el) => {
                field.ref(el);
                editorRef.current = el;
              }}
              onChange={(state) => {
                field.onChange(JSON.stringify(state));
              }}
              placeholder="e.g. “Track sentiment around Ethereum ETF by monitoring up to 15 Twitter accounts daily. Updated hourly.”"
              theme="textarea"
              onFocusOutside={field.onBlur}
              rich
              errorMessage={fieldState.error?.message}
              label={'Stream description'}
            />
          );
        }}
      />

      <div className={formInfoBlock}>
        <h3 className={formInfoTitle}>Buyer input setup</h3>
        <p className={formInfoDescription}>
          Buyers will be able to fill in their own targets—like Twitter
          accounts, wallet addresses, or websites—after purchasing this stream.
          You simply define:
        </p>
      </div>

      <Input
        {...register('customTargetsData.targetType', {
          required: 'This field is required',
          maxLength: {
            value: 100,
            message: 'Target type cannot exceed 100 characters',
          },
          validate: (v) => (v ? v.trim().length > 0 : undefined),
        })}
        maxLength={100}
        errorMessage={formState.errors.customTargetsData?.targetType?.message}
        placeholder={'e.g. “Twitter accounts, wallet addresses, websites”'}
        label={'What targets you want the buyer to provide?'}
      />

      <Input
        {...register('customTargetsData.targetTypeInstruction', {
          required: 'This field is required',
          maxLength: {
            value: 100,
            message: 'Instruction cannot exceed 100 characters',
          },
          validate: (v) => (v ? v.trim().length > 0 : undefined),
        })}
        maxLength={100}
        errorMessage={
          formState.errors.customTargetsData?.targetTypeInstruction?.message
        }
        placeholder={
          'e.g. “Submit twitter handles in lowercase without leading @”'
        }
        label={'Instructions for buyers'}
      />

      <Input
        {...register('customTargetsData.targetValidationRegex', {
          validate: (v) => {
            if (!v || v.trim().length === 0) {
              return undefined;
            }

            try {
              new RegExp(v);
              return true;
            } catch {
              return 'Invalid regex format';
            }
          },
        })}
        errorMessage={
          formState.errors.customTargetsData?.targetValidationRegex?.message
        }
        placeholder={'e.g. "^[a-z0-9_]+$"'}
        label={'Target validation regex'}
        before={<div className={regexInputDelimiter}>/</div>}
        after={<div className={regexInputDelimiter}>/</div>}
        markOptional
      />

      <div className={formInfoBlock}>
        <h3 className={formInfoTitle}>
          Customize how many items buyers can submit
        </h3>
        <p className={formInfoDescription}>
          How many items can the buyer submit?
        </p>
      </div>

      <div className={rowFields}>
        <Input
          {...register('customTargetsData.minTargets', {
            required: 'This field is required',
            validate: (value) =>
              (value! >= TARGETS_MIN_ALLOWED_VALUE &&
                value! < TARGETS_MAX_ALLOWED_VALUE) ||
              `Must be greater than ${TARGETS_MIN_ALLOWED_VALUE - 1} and less than ${TARGETS_MAX_ALLOWED_VALUE}`,
            valueAsNumber: true,
          })}
          type={'number'}
          errorMessage={formState.errors.customTargetsData?.minTargets?.message}
          placeholder={'e.g. 5'}
          label={'Minimum'}
        />

        <Input
          {...register('customTargetsData.maxTargets', {
            required: 'This field is required',
            validate: (value) =>
              (value! > TARGETS_MIN_ALLOWED_VALUE &&
                value! <= TARGETS_MAX_ALLOWED_VALUE) ||
              `Must be greater than ${TARGETS_MIN_ALLOWED_VALUE} and less or equal to ${TARGETS_MAX_ALLOWED_VALUE}`,
            valueAsNumber: true,
          })}
          type={'number'}
          errorMessage={formState.errors.customTargetsData?.maxTargets?.message}
          placeholder={'e.g. 50'}
          label={'Maximum'}
        />
      </div>

      <Input
        {...register('customTargetsData.maxTotalTargets', {
          validate: (value) => {
            if (!value) return undefined;

            return (
              value <= TOTAL_TARGETS_MAX_ALLOWED_VALUE ||
              `Must be less or equal to ${TOTAL_TARGETS_MAX_ALLOWED_VALUE}`
            );
          },
          valueAsNumber: true,
          setValueAs: (v) => (!v ? null : v),
        })}
        type={'number'}
        errorMessage={
          formState.errors.customTargetsData?.maxTotalTargets?.message
        }
        placeholder={'e.g. 500'}
        label={'Total available slots'}
        hint={
          'Useful when your backend has a global capacity limit. ' +
          'For example, if your system supports tracking up to 500 accounts ' +
          'total across all buyers. You can update this later if needed.'
        }
        markOptional
      />

      <div
        className={clsx(
          streamFormActions,
          !hasInitialValues && createStreamFormActions,
        )}
      >
        <Button
          disabled={!formState.isValid}
          type={'button'}
          onClick={handleNext}
          size={isTablet ? 'large' : 'medium'}
        >
          Next
        </Button>
      </div>
    </div>
  );
};
